import { Component, inject, signal } from '@angular/core';
import {
	Host,
	LuzmoService,
	MenuItem,
	Tenant,
	UserService,
	generateMenu,
	WeekView,
	saveToLocalStorage,
	getFromLocalStorage,
	User,
	NavigationComponent,
} from '@everysens/frontend-shared-v2';
import { environment } from '@rtls-environment/environment';
import Hotjar from '@hotjar/browser';
import { CabaneService } from '@rtls/core/cabane/cabane.service';
import { LocaleService } from '@everysens/ui';
import { HeapAnalyticsService } from '@everysens/heap-analytics';
import { PreferencesService } from './core/preferences/preferences.service';
import { TenantConfigurationService } from './core/tenant/configuration/tenant-configuration.service';
import { TmsService } from './tms.service';
import { Observable, forkJoin, of, switchMap, tap, iif } from 'rxjs';
import { CabaneTenantConfigurationDTO, UserPreferencesDTO } from 'data';
import { SupportedLocale } from './core/locale/locale.enum';
import { catchError } from 'rxjs/operators';
import { Bind } from 'primeng/bind';
import { Toast } from 'primeng/toast';
import { RouterOutlet } from '@angular/router';
import { NgProgressbar } from 'ngx-progressbar';
import { registerLocaleData } from '@angular/common';
import { datadogRum } from '@datadog/browser-rum';

@Component({
	selector: 'es-root',
	templateUrl: './app.component.html',
	styleUrls: ['./app.component.scss'],
	imports: [Bind, Toast, NavigationComponent, RouterOutlet, NgProgressbar],
})
export class AppComponent {
	protected readonly menuItems = signal<MenuItem[]>([]);
	private readonly userService = inject(UserService);
	private readonly localeService = inject(LocaleService);
	private readonly heapAnalyticsService = inject(HeapAnalyticsService);
	private readonly preferencesService = inject(PreferencesService);
	private readonly cabaneService = inject(CabaneService);
	private readonly tenantConfigurationService = inject(TenantConfigurationService);
	private readonly luzmoService = inject(LuzmoService);
	private readonly tmsService = inject(TmsService);

	constructor() {
		this.init();
		Hotjar.init(Number(environment.hotjar), Number(6));
	}

	private init() {
		this.userService
			.loadUser()
			.pipe(
				tap((user) => this.initHeap(user)),
				tap((user) => this.localeService.setLocale(user.locale as SupportedLocale)),
				tap((user) => this.handleUserLangReceived(user.locale)),
				switchMap((user) => {
					return forkJoin([
						this.preferencesService.loadPreferences(),
						this.getCabaneTenantConfiguration(user),
						this.tenantConfigurationService.loadTenantConfiguration(),
						this.getLuzmoItems(user),
					]);
				}),
				tap(([preferences, [cabaneConfig, user], rtlsConfig, luzmoItems]) => {
					this.setupDatadogRum(user);
					this.setDefaultLocale(user);
					this.setDefaultTenant(user);
					this.setDefaultWeekView(preferences);

					const host = environment.name === 'local' ? 'http://everysens.localhost:4200' : environment.baseApp;

					const menuItems = generateMenu({
						host: host as Host,
						appName: 'app',
						cabaneConfig: cabaneConfig?.configuration ?? {},
						rtlsConfig: rtlsConfig?.value ?? [],
						permissions: user.permissions,
						profile: user.profile?.type,
						luzmoItems: luzmoItems,
						preferences: preferences.value,
					});

					this.menuItems.set(menuItems);
				}),
			)
			.subscribe();
	}

	private getLuzmoItems(user: User): Observable<{ id: string; name: string }[]> {
		return iif(
			() => user.permissions.includes('read:tvms.data.insight.access'),
			this.luzmoService.getDashboards(),
			of([]),
		);
	}

	private getCabaneTenantConfiguration(user: User): Observable<[CabaneTenantConfigurationDTO | null, User]> {
		const tenant = this.getTenant(user);

		return this.cabaneService.getTenantConfiguration(tenant.uuid).pipe(
			switchMap((config) => {
				return forkJoin([
					of(config),
					this.userService.loadUserWithProfile(user, config?.configuration?.tms?.enabled ?? false),
				]);
			}),
			tap(([, user]) => this.tmsService.setTmsRoute(user)),
			catchError(() => {
				return of<[null, User]>([null, user]);
			}),
		);
	}

	private initHeap(user: User): void {
		const tenant = this.getTenant(user);
		this.heapAnalyticsService.addUserProperties({
			module: 'visibility',
			tenant: tenant.designation,
		});
	}

	private handleUserLangReceived(locale: string): void {
		const parsedLocale = locale.split('-')[0];
		const importedLocales: Record<string, () => Promise<{ default: unknown }>> = {
			de: () => import(`@angular/common/locales/de`),
			en: () => import(`@angular/common/locales/en`),
			fr: () => import(`@angular/common/locales/fr`),
			hu: () => import(`@angular/common/locales/hu`),
			ro: () => import(`@angular/common/locales/ro`),
			sk: () => import(`@angular/common/locales/sk`),
		};

		const localeImport = importedLocales[parsedLocale];
		if (localeImport) {
			void localeImport().then((lang) => {
				registerLocaleData(lang.default);
			});
		}
	}

	private setupDatadogRum(user: User): void {
		datadogRum.setUser({
			id: user.uuid,
			email: user.email,
			name: `${user.firstName} ${user.lastName}`,
			locale: user.locale,
			extUuid: user.externalUuid,
			profile: user.profile?.type,
		});

		const tenant = this.getTenant(user);

		datadogRum.setAccount({
			id: tenant.uuid,
			name: tenant.designation,
			label: tenant.label,
		});
	}

	private getTenant(user: User): Tenant {
		const tenantDesignation = getFromLocalStorage<string>('tenant');
		const tenant = user.tenants.find((t) => t.designation === tenantDesignation);

		return tenant ? tenant : user.primaryTenant;
	}

	private setDefaultLocale(user: User): void {
		const storedLocale = localStorage.getItem('locale');
		if (storedLocale) return;

		localStorage.setItem('locale', user.locale);
	}

	private setDefaultTenant(user: User): void {
		const storedTenantDesignation = getFromLocalStorage<string>('tenant');
		if (storedTenantDesignation) return;

		const tenant = this.getTenant(user);
		saveToLocalStorage('tenant', tenant.designation);
	}

	private setDefaultWeekView(preferences: UserPreferencesDTO): void {
		const weekView = preferences?.value?.preferences?.find((o): o is { weekView: WeekView } => {
			return typeof o === 'object' && o !== null && Object.hasOwn(o, 'weekView');
		})?.weekView ?? { id: 0, day: 'SUNDAY' };

		saveToLocalStorage('weekView', weekView);
	}
}
